import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { Loader2, Save, Sparkles, Grid3X3 } from "lucide-react";

const DURUM_OPTIONS = ["Güçlü", "Aktif", "Hassas", "Destekleniyor", "Dikkat"];
const ENERJI_ALANLARI = ["Ruhsal Enerji", "Zihinsel Hareketlilik", "Duygusal Denge", "Sosyal Enerji", "Karar Alma"];

type EnerjiAlani = { alan: string; durum: string; yorum: string };

type Kombinasyon = {
  id?: string;
  kisisel_gun: number;
  evrensel_gun: number;
  gun_temasi: string;
  ana_yorum: string;
  renk_enerjisi: string;
  ruhsal_odak: string;
  enerji_uyarisi: string;
  golge_enerji: string;
  destekleyici_davranis: string;
  bedensel_enerji: string;
  gunluk_rehberlik: string[];
  gunluk_gorev: string;
  olumlama: string;
  enerji_alanlari: EnerjiAlani[];
};

const emptyKombin = (kg: number, eg: number): Kombinasyon => ({
  kisisel_gun: kg,
  evrensel_gun: eg,
  gun_temasi: "",
  ana_yorum: "",
  renk_enerjisi: "",
  ruhsal_odak: "",
  enerji_uyarisi: "",
  golge_enerji: "",
  destekleyici_davranis: "",
  bedensel_enerji: "",
  gunluk_rehberlik: ["", "", ""],
  gunluk_gorev: "",
  olumlama: "",
  enerji_alanlari: ENERJI_ALANLARI.map(alan => ({ alan, durum: "Aktif", yorum: "" })),
});

async function fetchAllKombinasyonlar(): Promise<Kombinasyon[]> {
  const res = await adminFetch("/api/admin/numeroloji/kombinasyonlar");
  if (!res.ok) throw new Error("Veri alınamadı");
  return res.json();
}

async function saveKombinasyon(data: Kombinasyon): Promise<void> {
  const res = await adminFetch("/api/admin/numeroloji/kombinasyonlar", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err?.error ?? "Kaydedilemedi");
  }
}

async function generateWithAI(kg: number, eg: number): Promise<Kombinasyon> {
  const res = await adminFetch("/api/admin/numeroloji/kombinasyonlar/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kisisel_gun: kg, evrensel_gun: eg }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err?.error ?? "AI üretimi başarısız");
  }
  return res.json();
}

export default function AdminNumerolojiKombinasyonlar() {
  const { sessionQuery, loginMutation } = useAdminSession();
  const authenticated = Boolean(sessionQuery.data?.authenticated);
  const configured = Boolean(sessionQuery.data?.configured);
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const [allData, setAllData] = useState<Kombinasyon[]>([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [selected, setSelected] = useState<{ kg: number; eg: number } | null>(null);
  const [form, setForm] = useState<Kombinasyon | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) return;
    fetchAllKombinasyonlar()
      .then(setAllData)
      .catch(() => setAllData([]))
      .finally(() => setLoadingAll(false));
  }, [authenticated]);

  const filledSet = new Set(allData.map(k => `${k.kisisel_gun}-${k.evrensel_gun}`));

  const handleCellClick = (kg: number, eg: number) => {
    setSelected({ kg, eg });
    const existing = allData.find(k => k.kisisel_gun === kg && k.evrensel_gun === eg);
    setForm(existing ? { ...existing, gunluk_rehberlik: existing.gunluk_rehberlik ?? ["", "", ""], enerji_alanlari: existing.enerji_alanlari ?? ENERJI_ALANLARI.map(alan => ({ alan, durum: "Aktif", yorum: "" })) } : emptyKombin(kg, eg));
    setSaveMsg(null);
  };

  const setField = (key: keyof Kombinasyon, val: any) => {
    setForm(f => f ? { ...f, [key]: val } : f);
  };

  const setRehberlik = (i: number, val: string) => {
    setForm(f => {
      if (!f) return f;
      const r = [...f.gunluk_rehberlik];
      r[i] = val;
      return { ...f, gunluk_rehberlik: r };
    });
  };

  const setEnerjiAlani = (i: number, key: keyof EnerjiAlani, val: string) => {
    setForm(f => {
      if (!f) return f;
      const arr = [...f.enerji_alanlari];
      arr[i] = { ...arr[i], [key]: val };
      return { ...f, enerji_alanlari: arr };
    });
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      await saveKombinasyon(form);
      setAllData(prev => {
        const idx = prev.findIndex(k => k.kisisel_gun === form.kisisel_gun && k.evrensel_gun === form.evrensel_gun);
        if (idx >= 0) { const next = [...prev]; next[idx] = form; return next; }
        return [...prev, form];
      });
      setSaveMsg("Kaydedildi ✓");
    } catch (e: any) {
      setSaveMsg(`Hata: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true);
    setSaveMsg(null);
    try {
      const generated = await generateWithAI(selected.kg, selected.eg);
      setForm(generated);
      setSaveMsg("AI içeriği üretildi — kaydetmeyi unutmayın.");
    } catch (e: any) {
      setSaveMsg(`AI Hatası: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  if (!authenticated) return (
    <AdminLoginCard
      configured={configured}
      onLogin={(args) => loginMutation.mutate(args)}
      isLoading={loginMutation.isPending}
      error={loginMutation.error?.message}
    />
  );

  const inputCls = cn("w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-500", isDark ? "border-slate-700 bg-slate-800 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400");
  const textareaCls = cn(inputCls, "resize-none");
  const labelCls = cn("block text-xs font-semibold uppercase tracking-wide mb-1", isDark ? "text-slate-400" : "text-slate-500");

  return (
    <div className={cn("flex min-h-screen", isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900")}>
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center gap-3 border-b px-6 py-4 border-slate-800">
          <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
          <Grid3X3 className="h-5 w-5 text-violet-400" />
          <h1 className="text-lg font-bold">Günlük Kombinasyonlar (9×9)</h1>
          <span className="ml-2 text-xs text-slate-400">{allData.length}/81 dolu</span>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Grid */}
          <div className="w-[340px] shrink-0 border-r border-slate-800 p-4 overflow-auto">
            <div className="mb-3 text-xs text-slate-400">Satır = Kişisel Gün, Sütun = Evrensel Gün</div>
            {loadingAll ? (
              <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>
            ) : (
              <table className="border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="w-8 pb-1 text-slate-500">K\E</th>
                    {Array.from({ length: 9 }, (_, i) => (
                      <th key={i} className="w-8 pb-1 text-center text-slate-400">{i + 1}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 9 }, (_, ri) => (
                    <tr key={ri}>
                      <td className="pr-1 text-right text-slate-400 font-medium">{ri + 1}</td>
                      {Array.from({ length: 9 }, (_, ci) => {
                        const kg = ri + 1, eg = ci + 1;
                        const filled = filledSet.has(`${kg}-${eg}`);
                        const isSelected = selected?.kg === kg && selected?.eg === eg;
                        return (
                          <td key={ci} className="p-0.5">
                            <button
                              onClick={() => handleCellClick(kg, eg)}
                              className={cn(
                                "w-7 h-7 rounded text-[10px] font-bold transition-all",
                                isSelected ? "ring-2 ring-violet-400 scale-110" : "",
                                filled ? "bg-emerald-600 text-white hover:bg-emerald-500" : "bg-slate-700 text-slate-400 hover:bg-slate-600"
                              )}
                            >
                              {kg}/{eg}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Editor */}
          <div className="flex-1 overflow-auto p-6">
            {!form ? (
              <div className="flex h-full items-center justify-center text-slate-500">
                <div className="text-center">
                  <Grid3X3 className="mx-auto mb-3 h-10 w-10 opacity-30" />
                  <p>Sol taraftan bir kombinasyon seçin</p>
                </div>
              </div>
            ) : (
              <div className="max-w-2xl space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-bold">
                    Kişisel {form.kisisel_gun} × Evrensel {form.evrensel_gun}
                  </h2>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating} className="gap-2 border-violet-600 text-violet-400 hover:bg-violet-900/30">
                      {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      AI ile Üret
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving} className="gap-2 bg-violet-700 hover:bg-violet-600">
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      Kaydet
                    </Button>
                  </div>
                </div>
                {saveMsg && <p className={cn("text-sm", saveMsg.startsWith("Hata") ? "text-red-400" : "text-emerald-400")}>{saveMsg}</p>}

                <div>
                  <label className={labelCls}>Gün Teması</label>
                  <input className={inputCls} value={form.gun_temasi} onChange={e => setField("gun_temasi", e.target.value)} placeholder="max 5 kelime" />
                </div>

                {[
                  { key: "ana_yorum", label: "Ana Yorum", rows: 5 },
                  { key: "renk_enerjisi", label: "Renk Enerjisi", rows: 2 },
                  { key: "ruhsal_odak", label: "Ruhsal Odak", rows: 3 },
                  { key: "enerji_uyarisi", label: "Enerji Uyarısı", rows: 3 },
                  { key: "golge_enerji", label: "Gölge Enerji", rows: 3 },
                  { key: "destekleyici_davranis", label: "Destekleyici Davranış", rows: 3 },
                  { key: "bedensel_enerji", label: "Bedensel Enerji", rows: 3 },
                ].map(({ key, label, rows }) => (
                  <div key={key}>
                    <label className={labelCls}>{label}</label>
                    <textarea className={textareaCls} rows={rows} value={(form as any)[key]} onChange={e => setField(key as keyof Kombinasyon, e.target.value)} />
                  </div>
                ))}

                <div>
                  <label className={labelCls}>Günlük Rehberlik (3 öneri)</label>
                  <div className="space-y-2">
                    {[0, 1, 2].map(i => (
                      <input key={i} className={inputCls} value={form.gunluk_rehberlik[i] ?? ""} onChange={e => setRehberlik(i, e.target.value)} placeholder={`Öneri ${i + 1}`} />
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Günlük Görev</label>
                  <input className={inputCls} value={form.gunluk_gorev} onChange={e => setField("gunluk_gorev", e.target.value)} />
                </div>

                <div>
                  <label className={labelCls}>Olumlama</label>
                  <input className={inputCls} value={form.olumlama} onChange={e => setField("olumlama", e.target.value)} />
                </div>

                <div>
                  <label className={labelCls}>Enerji Alanları</label>
                  <div className="space-y-4">
                    {form.enerji_alanlari.map((ea, i) => (
                      <div key={i} className={cn("rounded-lg border p-3 space-y-2", isDark ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50")}>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-violet-400 w-40 shrink-0">{ea.alan}</span>
                          <select
                            className={cn(inputCls, "w-40")}
                            value={ea.durum}
                            onChange={e => setEnerjiAlani(i, "durum", e.target.value)}
                          >
                            {DURUM_OPTIONS.map(d => <option key={d}>{d}</option>)}
                          </select>
                        </div>
                        <textarea className={textareaCls} rows={2} value={ea.yorum} onChange={e => setEnerjiAlani(i, "yorum", e.target.value)} placeholder="3-4 cümle yorum…" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
